import { useId } from "react";
import { Link } from "react-router";

import type { ChoiceGroup } from "./choices.ts";
import "./panelHead.css";

/**
 * The head of a panel of works: which collection is showing, and how to change it.
 *
 * The `<select>` sits INSIDE the `<h2>` rather than beside it. A switcher whose
 * current value is the collection's name is already the heading, and rendering
 * both would put the same words on screen twice; keeping the h2 around it means
 * the panel still has a heading in the document outline. With nothing to switch
 * between the name is plain text — a control with one option is chrome
 * pretending to be a control.
 *
 * Shared by the dashboard panel and the collection grid, so the same switcher
 * sits in the same place on both screens. What differs between them is passed
 * in: the grid groups its options and has nowhere to send anyone, the dashboard
 * has one flat run and a "View all".
 */
export function CollectionHead({
  name,
  groups,
  selected,
  label,
  link,
  onSelect,
}: {
  /** What is showing, for when there is nothing to switch between. */
  name: string;
  /** Empty when there is nothing to switch between. */
  groups: ChoiceGroup[];
  selected: string;
  /** Names the switcher for a screen reader; the value it shows is the name. */
  label: string;
  /** Where the head sends the reader for more, or null when this IS the fuller list. */
  link: { href: string; label: string } | null;
  onSelect: (key: string) => void;
}) {
  const pickerId = useId();

  return (
    <div className="panel-head">
      <h2 className="panel-head__title">
        {groups.length > 0 ? (
          <>
            <label className="panel-head__label" htmlFor={pickerId}>
              {label}
            </label>
            <select
              id={pickerId}
              className="panel-head__select"
              value={selected}
              onChange={(event) => onSelect(event.target.value)}
            >
              {groups.map((group, index) =>
                group.label === null ? (
                  // Keyed by position: an unlabelled run has no name to key on,
                  // and the groups are rebuilt whole on every collection load.
                  <Options key={`plain-${String(index)}`} group={group} />
                ) : (
                  <optgroup key={group.label} label={group.label}>
                    <Options group={group} />
                  </optgroup>
                ),
              )}
            </select>
          </>
        ) : (
          name
        )}
      </h2>

      {link && (
        <Link to={link.href} className="panel-head__link">
          {link.label} <span aria-hidden="true">→</span>
        </Link>
      )}
    </div>
  );
}

function Options({ group }: { group: ChoiceGroup }) {
  return (
    <>
      {group.choices.map((choice) => (
        <option key={choice.key} value={choice.key}>
          {choice.label}
        </option>
      ))}
    </>
  );
}
